// src/clients/RaceClient.ts
import { BaseClient } from './BaseClient';
import { resolveRaceApiBaseUrl } from './locationApiBase';
import type { ActivityOption } from '../models/entitlement/ActivityOption';
import type { SlotOption } from '../models/entitlement/SlotOption';
import type { RedeemEntitlementRequestBody } from '../models/requestBodies/RedeemEntitlementRequestBody';
import type { RedeemResult, RedeemedBooking } from '../models/entitlement/RedeemResult';

interface ExperienceDto { id: string; name: string; activityIds: string[]; }
interface ActivityDto { id: string; title: string; durationInHours: number; }
interface SlotDto {
  slotId: string; startTime: string; endTime: string;
  proRigsAvailable: number; esportsRigsAvailable: number;
}

export class RaceClient extends BaseClient {
  // public funnel GETs are anonymous; going through fetchWithAuth is harmless and consistent.
  public async getActivities(location: string): Promise<ActivityOption[]> {
    const base = resolveRaceApiBaseUrl(location);
    const expRes = await this.fetchWithAuth(`${base}public/experiences`, { method: 'GET' });
    if (!expRes.ok) throw new Error(`Failed to load experiences (${expRes.status})`);
    const experiences = (await expRes.json()) as ExperienceDto[];

    const options: ActivityOption[] = [];
    for (const exp of experiences) {
      const search = new URLSearchParams({ location, experienceId: exp.id });
      const actRes = await this.fetchWithAuth(`${base}public/bookings/activities?${search}`, { method: 'GET' });
      if (!actRes.ok) throw new Error(`Failed to load activities (${actRes.status})`);
      const activities = (await actRes.json()) as ActivityDto[];
      for (const a of activities) {
        options.push({ activityId: a.id, experienceId: exp.id, title: a.title, durationInHours: a.durationInHours });
      }
    }
    return options;
  }

  public async getDayAvailability(location: string, dateYmd: string, activityId: string, people = 1): Promise<SlotOption[]> {
    const base = resolveRaceApiBaseUrl(location);
    const search = new URLSearchParams({ location, date: dateYmd, activityId, people: String(people) });
    const res = await this.fetchWithAuth(`${base}public/bookings/availability/day?${search}`, { method: 'GET' });
    if (!res.ok) throw new Error(`Failed to load availability (${res.status})`);
    const payload = (await res.json()) as { slots?: SlotDto[] };
    return (payload.slots ?? []).map((s) => ({
      slotId: s.slotId, startTime: s.startTime, endTime: s.endTime,
      proRigsAvailable: s.proRigsAvailable, esportsRigsAvailable: s.esportsRigsAvailable,
    }));
  }

  public async redeemEntitlement(location: string, body: RedeemEntitlementRequestBody): Promise<RedeemResult> {
    const base = resolveRaceApiBaseUrl(location);
    // Content-Type: application/json is auto-set by BaseClient.fetchWithAuth for POST (house pattern).
    const res = await this.fetchWithAuth(`${base}public/bookings/entitlement`, {
      method: 'POST', body: JSON.stringify(body),
    });
    if (res.ok) {
      const booking = (await res.json()) as RedeemedBooking;
      return { ok: true, booking };
    }
    let error = 'Something went wrong. Please try again.';
    try {
      const parsed = (await res.json()) as { error?: string };
      if (parsed?.error) error = parsed.error;
    } catch {
      // non-JSON / empty body (e.g. raw 500) — keep the generic message
    }
    return { ok: false, status: res.status, error };
  }
}

const raceApi = new RaceClient();
export default raceApi;
